import { Breadcrumb } from '~/lib';
import { Product, PromotionType } from '~/lib/data-contract/salesforce';
import { PriceLike, SalesforceProduct } from '~/lib/salesforce/types';
import { TProductDetails } from '~/shared/components/ProductCard/ProductCard.definition';
import { PriceTypes } from '~/shared/components/ProductCard/components/Price';
import { ProductEventData, PROMOTION_EVENT_TYPE, PromotionEvent } from './models';
import { _transformProductsToGAItems } from './useGTMEvents/helper';

export const _transformProductDetailsToDataLayer = (
    hit: TProductDetails,
    quantity: number,
    position: number,
    isPlusMember?: boolean,
): ProductEventData => {
    if (!hit) return {} as ProductEventData;

    return {
        brand: hit.brand ?? '',
        category: hit?.primary_category_id ?? '',
        category2: hit?.cust_categories?.[1]?.name ?? '',
        category3: hit?.cust_categories?.[2]?.name ?? '',
        category4: hit?.cust_categories?.[3]?.name ?? '',
        category5: hit?.cust_categories?.[4]?.name ?? '',
        color: hit?.cust_vendor_color ?? '',
        ean: hit?.EAN ?? '',
        freightGroup: hit?.cust_freight_group ?? '',
        fullfillment: hit?.cust_fulfillment ?? '',
        id: hit?.id ?? '',
        name: hit?.longName ?? hit?.name,
        position,
        price: getEffectivePrice(hit.c_prices ?? hit.cust_prices, isPlusMember),
        quantity: quantity ?? hit?.quantity,
        queryId: hit?.__queryID ?? '',
        rating: hit?.rating?.rating.toString() ?? '',
        ratingCount: hit?.cust_yotpoReviewCount,
        ratingValue: hit?.cust_yotpoAverageRating,
        serie: hit?.cust_series ?? '',
        variant: hit?.cust_synopsis ?? '',
        c_prices: hit.c_prices,
        cust_prices: hit.cust_prices,
    };
};

/**
 * @deprecated Typed against the data-contract `Product`, which the Salesforce product does
 * not satisfy. Use `transformSalesforceProductToDataLayer` instead.
 */
export const _transformProductToDataLayer = (
    hit: Product,
    quantity?: number,
    position?: number,
    isPlusMember?: boolean,
): ProductEventData => {
    if (!hit) return {} as ProductEventData;
    const prices = hit?.cust_prices ?? hit.c_prices;

    return {
        brand: hit.brand ?? '',
        category: hit.c_ProductMainCategory ?? '',
        category2: hit.c_ProductGoogleCategoryName?.split(' > ')[1] ?? '',
        category3: hit.c_ProductGoogleCategoryName?.split(' > ')[2] ?? '',
        category4: hit.c_ProductGoogleCategoryName?.split(' > ')[3] ?? '',
        category5: hit.c_ProductGoogleCategoryName?.split(' > ')[4] ?? '',
        color: hit.c_ItemSupplierColour ?? '',
        ean: hit.ean ?? '',
        freightGroup: hit.c_ItemFreightGroup ?? '',
        fullfillment: hit.c_ItemFullfillment ?? '',
        id: hit.id ?? '',
        name: hit.longName ?? hit.name,
        position: position ?? 0,
        price: getEffectivePrice(prices, isPlusMember),
        quantity: quantity ?? hit.quantity,
        queryId: hit.__queryID ?? '',
        rating: hit.rating?.value.toString() ?? '',
        ratingCount: hit?.c_yotpoReviewCount ?? 0,
        ratingValue: hit?.c_yotpoAverageRating ?? 0,
        serie: hit.c_ProductSeries ?? '',
        variant: hit.c_synopsis ?? '',
        c_prices: hit?.c_prices,
        cust_prices: hit?.cust_prices,
        coupon: hit.coupon ?? '',
        stockStatus: hit?.c_in_stock_web,
    };
};

interface ProductDataLayerPayload {
    quantity?: number;
    isPlusMember?: boolean;
}

/**
 * Builds the data layer entry from a Salesforce product. Fields the product genuinely has no
 * answer for - list position, Algolia query id, coupon - are left out rather than faked.
 */
export const transformSalesforceProductToDataLayer = (
    product: SalesforceProduct,
    optionalPayload?: ProductDataLayerPayload,
): ProductEventData => {
    if (!product) return {} as ProductEventData;

    const { quantity = 1, isPlusMember } = optionalPayload ?? {};

    // Google's category path arrives as a single ' > ' delimited string.
    const googleCategories = product.c_ProductGoogleCategoryName?.split(' > ') ?? [];

    return {
        brand: product.brand ?? '',
        category: product.c_ProductMainCategory ?? '',
        category2: googleCategories[1] ?? '',
        category3: googleCategories[2] ?? '',
        category4: googleCategories[3] ?? '',
        category5: googleCategories[4] ?? '',
        color: product.c_ItemSupplierColour ?? '',
        ean: product.ean ?? '',
        freightGroup: product.c_itemFreightGroup ?? '',
        fullfillment: product.c_ItemFullfillment ?? '',
        id: product.id ?? '',
        name: product.name ?? '',
        price: getEffectivePrice(product.c_prices, isPlusMember),
        quantity,
        // Salesforce carries the Yotpo average rather than a separate rating object.
        rating: String(product.c_yotpoAverageRating ?? ''),
        ratingCount: product.c_yotpoReviewCount ?? 0,
        ratingValue: product.c_yotpoAverageRating ?? 0,
        serie: product.c_ProductSeries ?? '',
        variant: product.c_synopsis ?? '',
        c_prices: product.c_prices,
        stockStatus: product.c_in_stock_web,
    };
};

export function getEffectivePrice(hit?: PriceLike[], isPlusMember?: boolean) {
    const salePrice = hit?.find((p) => p.type === PriceTypes.SALE);
    const defaultPrice = hit?.find((p) => p.type === PriceTypes.DEFAULT);

    if (salePrice) {
        if (salePrice.promotype === PromotionType.MEMBER_PRICE && !isPlusMember) {
            return priceWithoutVAT(defaultPrice?.value);
        }
        return priceWithoutVAT(salePrice.value);
    }

    return priceWithoutVAT(defaultPrice?.value);
}

export function priceWithoutVAT(price: number | string | undefined, vat = 1.25) {
    if (!price) return 0;

    return Number((Number(price) / vat).toFixed(2));
}

export const getListName = (breadcrumb: Breadcrumb): string => {
    const { items } = breadcrumb;
    if (!items?.length) return '';
    return items[items.length - 1].title ?? '';
};

export const getCategorySlug = (breadcrumb: Breadcrumb): string => {
    const url = breadcrumb.items?.at(-1)?.url;
    if (!url) return '';

    return url.split('?')[0].split('/').filter(Boolean).at(-1) ?? '';
};

// Item shape must match add_to_cart/purchase so the impression joins to a later order
export const _promotionItems = (promotion: PromotionEvent, isPlusMember: boolean) => {
    if (promotion.type !== PROMOTION_EVENT_TYPE.SPONSORED_PLACEMENT) return undefined;

    return _transformProductsToGAItems([promotion.product], {
        isPlusMember,
        itemListId: promotion.itemListId,
        itemListName: promotion.itemListName,
    });
};
